import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ChevronRight,
  ChevronsDownUp,
  ChevronsUpDown,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Trash2,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { Switch } from '@/components/ui/switch';
import {
  platformNavMenuApi,
  type NavMenuNode,
  type NavMenuStatus,
  type NavMenuType,
} from '@/api/platform-nav-menu';
import { usePlatformNavStore } from '@/store/usePlatformNavStore';
import { useUIStore } from '@/store/useUIStore';
import { NavIcon } from '@/components/nav/NavIcon';
import {
  adminDrawerFooterClass,
  adminDrawerHeaderClass,
  adminDrawerShell,
  fieldClass,
} from '@/lib/admin-ui';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

export type NavMenuTreeManagerProps = {
  /** 与 nav_menus.scope 一致 */
  scope: 'PLATFORM' | 'TENANT';
  accent: 'cyan' | 'fuchsia';
  /** standalone：独立「菜单管理」页；embedded：嵌入「权限与菜单」 */
  variant: 'standalone' | 'embedded';
};

const drawerInputClass =
  'border-white/10 bg-zinc-900/60 text-zinc-100 placeholder:text-zinc-500 focus-visible:ring-cyan-500/35';
const drawerLabelClass = 'text-zinc-200';

const TYPE_LABEL: Record<NavMenuType, string> = {
  DIRECTORY: '目录',
  MENU: '菜单',
  BUTTON: '按钮',
  EXTERNAL: '外链',
};

const TYPE_BADGE: Record<NavMenuType, string> = {
  DIRECTORY: 'border-emerald-500/35 bg-emerald-500/10 text-emerald-200',
  MENU: 'border-cyan-500/35 bg-cyan-500/10 text-cyan-100',
  BUTTON: 'border-amber-500/35 bg-amber-500/12 text-amber-100',
  EXTERNAL: 'border-violet-500/35 bg-violet-500/10 text-violet-100',
};

type FormState = {
  parentId: string;
  menuType: NavMenuType;
  sortOrder: number;
  title: string;
  iconKey: string;
  permissionCode: string;
  allowAllPlatformAdmins: boolean;
  path: string;
  componentPath: string;
  externalUrl: string;
  visible: boolean;
  status: NavMenuStatus;
};

const emptyForm = (defaults?: Partial<FormState>): FormState => ({
  parentId: '',
  menuType: 'MENU',
  sortOrder: 0,
  title: '',
  iconKey: '',
  permissionCode: '',
  allowAllPlatformAdmins: false,
  path: '',
  componentPath: '',
  externalUrl: '',
  visible: true,
  status: 'NORMAL',
  ...defaults,
});

function collectExpandableIds(nodes: NavMenuNode[], out = new Set<string>()) {
  for (const n of nodes) {
    if (n.children.length > 0) out.add(n.id);
    collectExpandableIds(n.children, out);
  }
  return out;
}

function flattenRows(
  nodes: NavMenuNode[],
  depth: number,
  expanded: Set<string>,
  out: { node: NavMenuNode; depth: number }[] = [],
) {
  for (const n of nodes) {
    out.push({ node: n, depth });
    if (n.children.length > 0 && expanded.has(n.id)) {
      flattenRows(n.children, depth + 1, expanded, out);
    }
  }
  return out;
}

function filterTree(
  nodes: NavMenuNode[],
  q: string,
  status: 'all' | NavMenuStatus,
  vis: 'all' | 'yes' | 'no',
): NavMenuNode[] {
  const ql = q.trim().toLowerCase();
  return nodes.reduce<NavMenuNode[]>((acc, n) => {
    const ch = filterTree(n.children, q, status, vis);
    const titleOk = !ql || n.title.toLowerCase().includes(ql);
    const stOk = status === 'all' || n.status === status;
    const vOk =
      vis === 'all' ||
      (vis === 'yes' && n.visible) ||
      (vis === 'no' && !n.visible);
    const selfOk = titleOk && stOk && vOk;
    if (selfOk || ch.length > 0) {
      acc.push({ ...n, children: ch });
    }
    return acc;
  }, []);
}

function parentOptions(
  tree: NavMenuNode[],
  excludeId?: string,
): { id: string; label: string }[] {
  const blocked = new Set<string>();
  if (excludeId) {
    const find = (nodes: NavMenuNode[]): NavMenuNode | null => {
      for (const n of nodes) {
        if (n.id === excludeId) return n;
        const x = find(n.children);
        if (x) return x;
      }
      return null;
    };
    const mark = (n: NavMenuNode) => {
      blocked.add(n.id);
      n.children.forEach(mark);
    };
    const sub = find(tree);
    if (sub) mark(sub);
  }
  const out: { id: string; label: string }[] = [{ id: '', label: '（根级）' }];
  const walk = (nodes: NavMenuNode[], prefix: string) => {
    for (const n of nodes) {
      if (blocked.has(n.id)) continue;
      if (n.menuType === 'DIRECTORY' || n.menuType === 'MENU') {
        out.push({ id: n.id, label: `${prefix}${n.title}` });
      }
      if (n.children.length) walk(n.children, `${prefix}${n.title} / `);
    }
  };
  walk(tree, '');
  return out;
}

export function NavMenuTreeManager({
  scope,
  accent,
  variant,
}: NavMenuTreeManagerProps) {
  const isLight = useUIStore((s) => s.theme === 'light');
  const [tree, setTree] = useState<NavMenuNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [filterName, setFilterName] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | NavMenuStatus>('all');
  const [filterVisible, setFilterVisible] = useState<'all' | 'yes' | 'no'>('all');

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(() => emptyForm());
  const loadPlatformNav = usePlatformNavStore((s) => s.loadPlatformNav);

  const primaryClass =
    accent === 'fuchsia'
      ? 'bg-fuchsia-600/90 text-white hover:bg-fuchsia-500'
      : 'bg-cyan-600/90 text-white hover:bg-cyan-500';
  const rowActionHover =
    accent === 'fuchsia'
      ? 'hover:text-fuchsia-200'
      : 'hover:text-cyan-200';
  const visOkClass =
    accent === 'fuchsia'
      ? 'border-fuchsia-500/30 text-fuchsia-200'
      : 'border-cyan-500/30 text-cyan-200';
  const statusOkClass =
    accent === 'fuchsia'
      ? 'border-fuchsia-500/30 text-fuchsia-200'
      : 'border-cyan-500/30 text-cyan-200';
  const drawerCloseHover =
    accent === 'fuchsia'
      ? '[&>button]:hover:border-fuchsia-500/30 [&>button]:hover:bg-fuchsia-500/10 [&>button]:hover:text-fuchsia-200'
      : '[&>button]:hover:border-cyan-500/30 [&>button]:hover:bg-cyan-500/10 [&>button]:hover:text-cyan-200';
  const lightDrawerField =
    'border-slate-300 bg-white text-slate-800 placeholder:text-slate-400 focus-visible:ring-cyan-500/35';
  const lightDrawerLabel = 'text-slate-700';
  /** 抽屉底栏唯一主按钮：全宽胶囊 + 黑字（运营 cyan / 租户 fuchsia） */
  const drawerSaveBtnClass =
    accent === 'fuchsia'
      ? cn(
          'h-12 w-full rounded-full border-0 text-base font-medium text-zinc-950 shadow-none',
          'bg-fuchsia-500 hover:bg-fuchsia-400',
          'disabled:pointer-events-none disabled:opacity-45',
        )
      : cn(
          'h-12 w-full rounded-full border-0 text-base font-medium text-zinc-950 shadow-none',
          'bg-cyan-400 hover:bg-cyan-300',
          'disabled:pointer-events-none disabled:opacity-45',
        );

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await platformNavMenuApi.getTree(scope);
      setTree(data);
      setExpanded(collectExpandableIds(data));
    } catch {
      toast.error('加载菜单树失败');
      setTree([]);
    } finally {
      setLoading(false);
    }
  }, [scope]);

  useEffect(() => {
    void load();
  }, [load]);

  const filteredTree = useMemo(
    () => filterTree(tree, filterName, filterStatus, filterVisible),
    [tree, filterName, filterStatus, filterVisible],
  );

  const rows = useMemo(
    () => flattenRows(filteredTree, 0, expanded),
    [filteredTree, expanded],
  );

  const optsParent = useMemo(
    () => parentOptions(tree, editingId ?? undefined),
    [tree, editingId],
  );

  function openCreate(parentId?: string) {
    setEditingId(null);
    setForm(
      emptyForm({
        parentId: parentId ?? '',
        menuType: 'MENU',
        sortOrder: 0,
      }),
    );
    setDrawerOpen(true);
  }

  function openEdit(n: NavMenuNode) {
    setEditingId(n.id);
    setForm({
      parentId: n.parentId ?? '',
      menuType: n.menuType,
      sortOrder: n.sortOrder,
      title: n.title,
      iconKey: n.iconKey ?? '',
      permissionCode: n.permissionCode ?? '',
      allowAllPlatformAdmins: n.allowAllPlatformAdmins,
      path: n.path ?? '',
      componentPath: n.componentPath ?? '',
      externalUrl: n.externalUrl ?? '',
      visible: n.visible,
      status: n.status,
    });
    setDrawerOpen(true);
  }

  async function submitDrawer() {
    try {
      setSaving(true);
      const parentId = form.parentId.trim() || null;
      if (editingId) {
        await platformNavMenuApi.update(editingId, {
          parentId,
          menuType: form.menuType,
          sortOrder: form.sortOrder,
          title: form.title.trim(),
          iconKey: form.iconKey.trim() || null,
          permissionCode: form.permissionCode.trim() || null,
          allowAllPlatformAdmins: form.allowAllPlatformAdmins,
          path: form.path.trim() || null,
          componentPath: form.componentPath.trim() || null,
          externalUrl: form.externalUrl.trim() || null,
          visible: form.visible,
          status: form.status,
        });
        toast.success('已保存');
      } else {
        await platformNavMenuApi.create({
          scope,
          parentId,
          menuType: form.menuType,
          sortOrder: form.sortOrder,
          title: form.title.trim(),
          iconKey: form.iconKey.trim() || null,
          permissionCode: form.permissionCode.trim() || null,
          allowAllPlatformAdmins: form.allowAllPlatformAdmins,
          path: form.path.trim() || null,
          componentPath: form.componentPath.trim() || null,
          externalUrl: form.externalUrl.trim() || null,
          visible: form.visible,
          status: form.status,
        });
        toast.success('已创建');
      }
      setDrawerOpen(false);
      await load();
      if (scope === 'PLATFORM') await loadPlatformNav(true);
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? '保存失败';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(n: NavMenuNode) {
    if (!window.confirm(`确定删除「${n.title}」？`)) return;
    try {
      await platformNavMenuApi.remove(n.id);
      toast.success('已删除');
      await load();
      if (scope === 'PLATFORM') await loadPlatformNav(true);
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? '删除失败';
      toast.error(msg);
    }
  }

  function toggleExpand(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function expandAll() {
    setExpanded(collectExpandableIds(tree));
  }

  function collapseAll() {
    setExpanded(new Set());
  }

  return (
    <div className="space-y-6">
      {variant === 'standalone' && (
        <div>
          <h1 className="text-2xl font-bold text-foreground">菜单管理</h1>
          <p className="mt-1 max-w-3xl text-sm leading-relaxed text-muted-foreground">
            树形维护侧栏：目录仅分组；菜单打开站内路由；外链跳转站外；按钮用于权限点（不出现在侧栏）。修改后侧栏与角色 path
            白名单将使用库内数据。
          </p>
        </div>
      )}

      <Card className="border-white/10 bg-zinc-900/40">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">
            {variant === 'embedded'
              ? scope === 'PLATFORM'
                ? '侧栏菜单树（运营）'
                : '侧栏菜单树（租户）'
              : '筛选'}
          </CardTitle>
          <CardDescription>
            {variant === 'embedded'
              ? scope === 'PLATFORM'
                ? '在此维护运营侧栏菜单树。'
                : '在此维护租户门户侧栏菜单树。'
              : '在客户端过滤当前树；搜索为菜单名称包含匹配。'}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-3">
          <div className="space-y-1.5">
            <Label className="text-zinc-400">菜单名称</Label>
            <Input
              className="w-48 border-white/10 bg-zinc-900/70"
              value={filterName}
              onChange={(e) => setFilterName(e.target.value)}
              placeholder="关键词"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-zinc-400">菜单状态</Label>
            <Select
              value={filterStatus}
              onValueChange={(v) => setFilterStatus(v as 'all' | NavMenuStatus)}
            >
              <SelectTrigger className="w-36 border-white/10 bg-zinc-900/70">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">全部</SelectItem>
                <SelectItem value="NORMAL">正常</SelectItem>
                <SelectItem value="DISABLED">停用</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-zinc-400">是否显示</Label>
            <Select
              value={filterVisible}
              onValueChange={(v) => setFilterVisible(v as 'all' | 'yes' | 'no')}
            >
              <SelectTrigger className="w-36 border-white/10 bg-zinc-900/70">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">全部</SelectItem>
                <SelectItem value="yes">显示</SelectItem>
                <SelectItem value="no">隐藏</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button
            type="button"
            variant="outline"
            className="border-white/15"
            onClick={() => {
              setFilterName('');
              setFilterStatus('all');
              setFilterVisible('all');
            }}
          >
            重置
          </Button>
          <Button
            type="button"
            className={primaryClass}
            onClick={() => void load()}
          >
            <Search className="mr-1.5 h-4 w-4" />
            刷新
          </Button>
        </CardContent>
      </Card>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          className={primaryClass}
          onClick={() => openCreate()}
        >
          <Plus className="mr-1.5 h-4 w-4" />
          新增
        </Button>
        <Button
          type="button"
          variant="outline"
          className="border-white/15"
          onClick={expandAll}
        >
          <ChevronsUpDown className="mr-1.5 h-4 w-4" />
          展开全部
        </Button>
        <Button
          type="button"
          variant="outline"
          className="border-white/15"
          onClick={collapseAll}
        >
          <ChevronsDownUp className="mr-1.5 h-4 w-4" />
          折叠全部
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="ml-auto text-zinc-400"
          onClick={() => void load()}
          aria-label="刷新"
        >
          <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} />
        </Button>
      </div>

      <Card className="border-white/10 bg-zinc-900/40">
        <CardContent className="overflow-x-auto p-0 pt-4">
          {loading ? (
            <p className="px-6 pb-6 text-sm text-zinc-500">加载中…</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableHead>菜单名称</TableHead>
                  <TableHead className="w-[90px]">id</TableHead>
                  <TableHead>图标</TableHead>
                  <TableHead>类型</TableHead>
                  <TableHead className="w-[72px]">排序</TableHead>
                  <TableHead>路由地址</TableHead>
                  <TableHead>组件路径</TableHead>
                  <TableHead>权限标识</TableHead>
                  <TableHead>显示</TableHead>
                  <TableHead>状态</TableHead>
                  <TableHead className="w-[140px] text-right">操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableCell
                      colSpan={11}
                      className="py-14 text-center text-sm leading-relaxed text-zinc-500"
                    >
                      {tree.length === 0 ? (
                        <>
                          <p className="text-zinc-300">当前还没有任何菜单数据</p>
                          <p className="mt-2 max-w-lg mx-auto">
                            请点击上方「新增」添加根节点（建议先做「目录」再挂「菜单」）。若为新环境，请确认已执行数据库迁移且
                            <code className="mx-1 rounded bg-zinc-800 px-1.5 py-0.5 font-mono text-[11px] text-zinc-400">
                              nav_menus
                            </code>
                            表已就绪；也可在种子中预置菜单后再刷新。
                          </p>
                        </>
                      ) : (
                        <>
                          <p className="text-zinc-300">没有符合当前筛选条件的菜单</p>
                          <p className="mt-2">
                            可点击筛选卡片中的「重置」或放宽菜单名称 / 状态 / 显示条件。
                          </p>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ) : null}
                {rows.map(({ node: n, depth }) => (
                  <TableRow key={n.id} className="border-white/10">
                    <TableCell>
                      <div
                        className="flex items-center gap-1"
                        style={{ paddingLeft: depth * 16 }}
                      >
                        {n.children.length > 0 ? (
                          <button
                            type="button"
                            onClick={() => toggleExpand(n.id)}
                            className="rounded p-0.5 text-zinc-500 hover:bg-white/10 hover:text-zinc-200"
                            aria-label={expanded.has(n.id) ? '折叠' : '展开'}
                          >
                            <ChevronRight
                              className={cn(
                                'h-4 w-4 transition-transform',
                                expanded.has(n.id) && 'rotate-90',
                              )}
                            />
                          </button>
                        ) : (
                          <span className="inline-block w-5" />
                        )}
                        <span className="font-medium text-zinc-100">{n.title}</span>
                      </div>
                    </TableCell>
                    <TableCell
                      className="max-w-[90px] truncate font-mono text-[10px] text-zinc-500"
                      title={n.id}
                    >
                      {n.id.slice(0, 8)}…
                    </TableCell>
                    <TableCell>
                      <NavIcon name={n.iconKey} className="h-4 w-4 text-zinc-300" />
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={cn('text-xs', TYPE_BADGE[n.menuType])}
                      >
                        {TYPE_LABEL[n.menuType]}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-mono text-sm text-zinc-400">
                      {n.sortOrder}
                    </TableCell>
                    <TableCell className="max-w-[120px] truncate font-mono text-xs">
                      {n.path ?? '—'}
                    </TableCell>
                    <TableCell className="max-w-[140px] truncate font-mono text-[11px] text-zinc-500">
                      {n.componentPath ?? '—'}
                    </TableCell>
                    <TableCell className="max-w-[180px] truncate font-mono text-[11px] text-zinc-400">
                      {n.permissionCode ?? '—'}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={cn(
                          'text-xs',
                          n.visible
                            ? visOkClass
                            : 'border-red-500/30 text-red-300',
                        )}
                      >
                        {n.visible ? '显示' : '隐藏'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={cn(
                          'text-xs',
                          n.status === 'NORMAL' ? statusOkClass : 'border-zinc-600 text-zinc-400',
                        )}
                      >
                        {n.status === 'NORMAL' ? '正常' : '停用'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-0.5">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className={cn('h-8 w-8 text-zinc-400', rowActionHover)}
                          aria-label="新增子项"
                          onClick={() => openCreate(n.id)}
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className={cn('h-8 w-8 text-zinc-400', rowActionHover)}
                          aria-label="编辑"
                          onClick={() => openEdit(n)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-zinc-400 hover:text-red-400"
                          aria-label="删除"
                          onClick={() => void handleDelete(n)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Drawer
        open={drawerOpen}
        onOpenChange={(o) => setDrawerOpen(o)}
        direction="right"
      >
        <DrawerContent
          className={cn(
            adminDrawerShell('detail'),
            'border-0 p-0 [&>button]:!right-5 [&>button]:!top-5 [&>button]:rounded-xl [&>button]:border [&>button]:p-2 [&>button]:opacity-100',
            isLight
              ? '[&>button]:border-slate-300 [&>button]:bg-white [&>button]:text-slate-500 [&>button]:hover:border-cyan-400/45 [&>button]:hover:bg-cyan-50 [&>button]:hover:text-cyan-700'
              : '[&>button]:border-white/10 [&>button]:bg-zinc-900/90 [&>button]:text-zinc-400',
            drawerCloseHover,
          )}
        >
          <DrawerHeader
            className={cn(
              adminDrawerHeaderClass,
              isLight ? 'border-b border-slate-200 bg-white' : 'border-b border-white/10',
            )}
          >
            <DrawerTitle className={cn(isLight ? 'text-slate-900' : 'text-zinc-50')}>
              {editingId ? '编辑菜单' : '新增菜单'}
            </DrawerTitle>
            <DrawerDescription className={cn(isLight ? 'text-slate-500' : 'text-zinc-400')}>
              外链须填路由标识与 URL；按钮不参与侧栏展示。图标填 Lucide 组件名（如 LayoutDashboard）。
            </DrawerDescription>
          </DrawerHeader>
          <div
            className={cn(
              'min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 py-4',
              isLight ? 'bg-white' : 'bg-zinc-950',
            )}
          >
            <div className="grid gap-4">
              <div className="space-y-2">
                <Label className={cn(drawerLabelClass, isLight && lightDrawerLabel)}>父节点</Label>
                <Select
                  value={form.parentId || '__root__'}
                  onValueChange={(v) =>
                    setForm((f) => ({ ...f, parentId: v === '__root__' ? '' : v }))
                  }
                >
                  <SelectTrigger className={fieldClass}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {optsParent.map((o) => (
                      <SelectItem
                        key={o.id || 'root'}
                        value={o.id || '__root__'}
                      >
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className={cn(drawerLabelClass, isLight && lightDrawerLabel)}>类型</Label>
                <Select
                  value={form.menuType}
                  onValueChange={(v) =>
                    setForm((f) => ({ ...f, menuType: v as NavMenuType }))
                  }
                >
                  <SelectTrigger className={fieldClass}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="DIRECTORY">目录（仅分组）</SelectItem>
                    <SelectItem value="MENU">菜单（站内页）</SelectItem>
                    <SelectItem value="EXTERNAL">外链</SelectItem>
                    <SelectItem value="BUTTON">按钮（权限点）</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className={cn(drawerLabelClass, isLight && lightDrawerLabel)}>排序</Label>
                <Input
                  type="number"
                  className={cn(drawerInputClass, isLight && lightDrawerField)}
                  value={form.sortOrder}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      sortOrder: Number.parseInt(e.target.value, 10) || 0,
                    }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label className={cn(drawerLabelClass, isLight && lightDrawerLabel)}>菜单名称</Label>
                <Input
                  className={cn(drawerInputClass, isLight && lightDrawerField)}
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label className={cn(drawerLabelClass, isLight && lightDrawerLabel)}>图标（Lucide 名称）</Label>
                <Input
                  className={cn(drawerInputClass, isLight && lightDrawerField)}
                  value={form.iconKey}
                  onChange={(e) => setForm((f) => ({ ...f, iconKey: e.target.value }))}
                  placeholder="LayoutDashboard"
                />
              </div>
              <div
                className={cn(
                  'flex items-center justify-between gap-4 rounded-lg border px-3 py-2',
                  isLight ? 'border-slate-300 bg-slate-50' : 'border-white/10 bg-zinc-900/40',
                )}
              >
                <div>
                  <Label className={cn(drawerLabelClass, isLight && lightDrawerLabel)}>
                    {scope === 'PLATFORM'
                      ? '凡平台管理员可见'
                      : '凡租户成员可见（不校验单一权限码）'}
                  </Label>
                  <p className={cn('text-xs', isLight ? 'text-slate-500' : 'text-zinc-500')}>
                    {scope === 'PLATFORM'
                      ? '不校验单一权限码（如说明类菜单）'
                      : '勾选后登录租户门户的账号均可看到该项（仍可与权限码组合使用）'}
                  </p>
                </div>
                <Switch
                  checked={form.allowAllPlatformAdmins}
                  onCheckedChange={(v) =>
                    setForm((f) => ({ ...f, allowAllPlatformAdmins: v }))
                  }
                  checkedLabel="开启"
                  uncheckedLabel="关闭"
                />
              </div>
              <div className="space-y-2">
                <Label className={cn(drawerLabelClass, isLight && lightDrawerLabel)}>权限标识</Label>
                <Input
                  className={cn(drawerInputClass, 'font-mono text-sm', isLight && lightDrawerField)}
                  value={form.permissionCode}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, permissionCode: e.target.value }))
                  }
                  placeholder={
                    scope === 'PLATFORM'
                      ? 'platform:example:view'
                      : 'tenant:example:view'
                  }
                />
              </div>
              <div className="space-y-2">
                <Label className={cn(drawerLabelClass, isLight && lightDrawerLabel)}>路由地址 / 标识</Label>
                <Input
                  className={cn(drawerInputClass, 'font-mono text-sm', isLight && lightDrawerField)}
                  value={form.path}
                  onChange={(e) => setForm((f) => ({ ...f, path: e.target.value }))}
                  placeholder="/example"
                />
              </div>
              <div className="space-y-2">
                <Label className={cn(drawerLabelClass, isLight && lightDrawerLabel)}>组件路径（说明用）</Label>
                <Input
                  className={cn(drawerInputClass, isLight && lightDrawerField)}
                  value={form.componentPath}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, componentPath: e.target.value }))
                  }
                  placeholder="pages/ExamplePage"
                />
              </div>
              {form.menuType === 'EXTERNAL' && (
                <div className="space-y-2">
                  <Label className={cn(drawerLabelClass, isLight && lightDrawerLabel)}>外链 URL</Label>
                  <Input
                    className={cn(drawerInputClass, isLight && lightDrawerField)}
                    value={form.externalUrl}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, externalUrl: e.target.value }))
                    }
                    placeholder="https://"
                  />
                </div>
              )}
              <div
                className={cn(
                  'flex items-center justify-between gap-4 rounded-lg border px-3 py-2',
                  isLight ? 'border-slate-300 bg-slate-50' : 'border-white/10 bg-zinc-900/40',
                )}
              >
                <Label className={cn(drawerLabelClass, isLight && lightDrawerLabel)}>是否显示</Label>
                <Switch
                  checked={form.visible}
                  onCheckedChange={(v) => setForm((f) => ({ ...f, visible: v }))}
                  checkedLabel="显示"
                  uncheckedLabel="隐藏"
                />
              </div>
              <div className="space-y-2">
                <Label className={cn(drawerLabelClass, isLight && lightDrawerLabel)}>菜单状态</Label>
                <Select
                  value={form.status}
                  onValueChange={(v) =>
                    setForm((f) => ({ ...f, status: v as NavMenuStatus }))
                  }
                >
                  <SelectTrigger className={fieldClass}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="NORMAL">正常</SelectItem>
                    <SelectItem value="DISABLED">停用</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DrawerFooter
            className={cn(
              adminDrawerFooterClass,
              '!flex-col gap-0 px-4 py-4',
              isLight ? 'border-slate-200 bg-white' : 'border-white/10',
            )}
          >
            <Button
              type="button"
              variant="default"
              className={drawerSaveBtnClass}
              disabled={saving || !form.title.trim()}
              onClick={() => void submitDrawer()}
            >
              {saving ? '保存中…' : '保存'}
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </div>
  );
}
